import React, { useId, useState } from 'react';
import { format } from 'date-fns';
import BhTimeSelect from '../../components/settings/pages/business-hours/BhTimeSelect';
import BhModal from '../../components/settings/pages/business-hours/BhModal';
import CopyIcon from '../../components/settings/pages/business-hours/CopyIcon';
import {
  BUSINESS_DAYS,
  BUSINESS_DAY_LABELS,
} from '../business-hours/locationBusinessHoursUtils';
import '../../components/settings/pages/business-hours/BusinessHoursLayout.css';
import '../../components/settings/pages/payment-subsettings/PaymentSubsettings.css';
import './LocationsSettings.css';

function TimeField({ id, label, value, onDateChange, disabled = false }) {
  return (
    <BhTimeSelect
      id={id}
      label={label}
      value={value}
      onDateChange={onDateChange}
      disabled={disabled}
    />
  );
}

const LocationBusinessHoursEditor = ({
  hours,
  onChange,
  openOnHolidays = false,
  onOpenOnHolidaysChange,
  disabled = false,
}) => {
  const copyHoursTitleId = useId();
  const copyBreakTitleId = useId();
  const [copyDialog, setCopyDialog] = useState({
    open: false,
    sourceDay: '',
    hours: { open: null, close: null },
  });
  const [breakCopyDialog, setBreakCopyDialog] = useState({
    open: false,
    sourceDay: '',
    breakTimes: {
      breakStart: null,
      breakEnd: null,
      hasBreak: false,
    },
  });
  const [selectedDays, setSelectedDays] = useState({});

  const updateDay = (dayKey, field, value) => {
    onChange?.({
      ...hours,
      [dayKey]: {
        ...hours[dayKey],
        [field]: value,
      },
    });
  };

  const handleOpenCopyDialog = (dayKey) => {
    setSelectedDays({});
    setCopyDialog({
      open: true,
      sourceDay: dayKey,
      hours: {
        open: hours[dayKey]?.open,
        close: hours[dayKey]?.close,
      },
    });
  };

  const handleApplyHours = () => {
    const updatedHours = { ...hours };
    Object.keys(selectedDays).forEach((dayKey) => {
      if (selectedDays[dayKey] && dayKey !== copyDialog.sourceDay) {
        updatedHours[dayKey] = {
          ...updatedHours[dayKey],
          open: copyDialog.hours.open,
          close: copyDialog.hours.close,
        };
      }
    });
    onChange?.(updatedHours);
    setCopyDialog({ open: false, sourceDay: '', hours: { open: null, close: null } });
    setSelectedDays({});
  };

  const handleOpenBreakCopyDialog = (dayKey) => {
    setSelectedDays({});
    setBreakCopyDialog({
      open: true,
      sourceDay: dayKey,
      breakTimes: {
        breakStart: hours[dayKey]?.breakStart,
        breakEnd: hours[dayKey]?.breakEnd,
        hasBreak: hours[dayKey]?.hasBreak,
      },
    });
  };

  const handleApplyBreakTimes = () => {
    const updatedHours = { ...hours };
    Object.keys(selectedDays).forEach((dayKey) => {
      if (selectedDays[dayKey] && dayKey !== breakCopyDialog.sourceDay) {
        updatedHours[dayKey] = {
          ...updatedHours[dayKey],
          hasBreak: breakCopyDialog.breakTimes.hasBreak,
          breakStart: breakCopyDialog.breakTimes.breakStart,
          breakEnd: breakCopyDialog.breakTimes.breakEnd,
        };
      }
    });
    onChange?.(updatedHours);
    setBreakCopyDialog({
      open: false,
      sourceDay: '',
      breakTimes: { breakStart: null, breakEnd: null, hasBreak: false },
    });
    setSelectedDays({});
  };

  return (
    <div className="locations-settings__hours-editor bh-landing">
      <section className="bh-section bh-holiday-wrap">
        <h2 className="bh-section-title">Holidays</h2>
        <p className="bh-section-desc">
          Choose whether this location accepts bookings on recognized holidays.
        </p>
        <label className="ps-option-box" htmlFor="location-hours-open-holidays">
          <input
            id="location-hours-open-holidays"
            type="checkbox"
            className="ps-checkbox"
            checked={openOnHolidays}
            onChange={(event) => onOpenOnHolidaysChange?.(event.target.checked)}
            disabled={disabled}
          />
          <span className="ps-option-box__body">
            <span className="ps-option-box__title">Open on holidays</span>
            <p className="ps-option-box__hint">
              Tick the box if this location accepts bookings on recognized holidays.
            </p>
          </span>
        </label>
      </section>

      <section className="bh-section bh-section--divider">
        <h2 className="bh-section-title">Weekly schedule</h2>
        <p className="bh-section-desc">
          Set open and close times for this location only. Use copy to apply the same hours or break to multiple days.
        </p>

        <div className="bh-day-list">
          {BUSINESS_DAYS.map((dayKey) => {
            const dayLabel = BUSINESS_DAY_LABELS[dayKey];
            const safeDay = dayKey.replace(/\s+/g, '-');
            const dayHours = hours?.[dayKey] || {};
            const isOpen = dayHours.isOpen !== false;

            return (
              <article
                key={dayKey}
                className={`bh-day-card ${!isOpen ? 'bh-day-card--closed' : ''}`}
              >
                <div className="bh-day-card__header">
                  <h3 className="bh-day-name">{dayLabel}</h3>
                  <label className="bh-cell-tick">
                    <input
                      type="checkbox"
                      checked={isOpen}
                      onChange={(event) => updateDay(dayKey, 'isOpen', event.target.checked)}
                      disabled={disabled}
                      aria-label={isOpen ? `${dayLabel}: open, tick to close` : `${dayLabel}: closed, tick to open`}
                    />
                    <span className={`bh-cell-tick__text ${isOpen ? 'is-open' : 'is-closed'}`}>
                      {isOpen ? 'Open' : 'Closed'}
                    </span>
                  </label>
                </div>

                {!isOpen ? (
                  <p className="bh-day-closed-msg">Not accepting bookings this day.</p>
                ) : (
                  <div className="bh-day-card__body">
                    <div>
                      <div className="bh-block__head">
                        <span className="bh-block__label">Hours</span>
                        <button
                          type="button"
                          className="bh-icon-btn"
                          title="Copy hours to other days"
                          onClick={() => handleOpenCopyDialog(dayKey)}
                          disabled={disabled}
                          aria-label={`Copy ${dayLabel} hours`}
                        >
                          <CopyIcon />
                        </button>
                      </div>
                      <div className="bh-time-row">
                        <TimeField
                          id={`loc-open-${safeDay}`}
                          label="Open"
                          value={dayHours.open}
                          onDateChange={(value) => updateDay(dayKey, 'open', value)}
                          disabled={disabled}
                        />
                        <span className="bh-sep">–</span>
                        <TimeField
                          id={`loc-close-${safeDay}`}
                          label="Close"
                          value={dayHours.close}
                          onDateChange={(value) => updateDay(dayKey, 'close', value)}
                          disabled={disabled}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="bh-block__head">
                        <span className="bh-block__label">Break</span>
                        {dayHours.hasBreak ? (
                          <button
                            type="button"
                            className="bh-icon-btn"
                            title="Copy break to other days"
                            onClick={() => handleOpenBreakCopyDialog(dayKey)}
                            disabled={disabled}
                            aria-label={`Copy ${dayLabel} break`}
                          >
                            <CopyIcon />
                          </button>
                        ) : null}
                      </div>
                      <label
                        className="bh-cell-tick bh-cell-tick--single-line"
                        style={{ marginBottom: dayHours.hasBreak ? '0.75rem' : 0 }}
                        title={dayHours.hasBreak ? 'Untick to remove break' : 'Tick to add a break'}
                      >
                        <input
                          type="checkbox"
                          checked={dayHours.hasBreak || false}
                          onChange={(event) => updateDay(dayKey, 'hasBreak', event.target.checked)}
                          disabled={disabled}
                          aria-label={`Break on ${dayLabel}`}
                        />
                        <span
                          className={`bh-cell-tick__text ${dayHours.hasBreak ? 'is-open' : 'is-closed'}`}
                        >
                          Scheduled break
                        </span>
                      </label>

                      {dayHours.hasBreak ? (
                        <div className="bh-time-row">
                          <TimeField
                            id={`loc-break-start-${safeDay}`}
                            label="Start"
                            value={dayHours.breakStart}
                            onDateChange={(value) => updateDay(dayKey, 'breakStart', value)}
                            disabled={disabled}
                          />
                          <span className="bh-sep">–</span>
                          <TimeField
                            id={`loc-break-end-${safeDay}`}
                            label="End"
                            value={dayHours.breakEnd}
                            onDateChange={(value) => updateDay(dayKey, 'breakEnd', value)}
                            disabled={disabled}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <BhModal
        open={copyDialog.open}
        onClose={() => {
          setCopyDialog((prev) => ({ ...prev, open: false }));
          setSelectedDays({});
        }}
        title="Copy business hours"
        titleId={copyHoursTitleId}
        footer={
          <>
            <button
              type="button"
              className="bh-btn bh-btn--ghost"
              onClick={() => {
                setCopyDialog((prev) => ({ ...prev, open: false }));
                setSelectedDays({});
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bh-btn bh-btn--primary"
              onClick={handleApplyHours}
              disabled={Object.values(selectedDays).filter(Boolean).length === 0}
            >
              Apply to selected
            </button>
          </>
        }
      >
        <p className="bh-modal-intro">Select days to receive these times:</p>
        <div className="bh-modal-summary">
          <p>
            <strong>Open:</strong>{' '}
            {copyDialog.hours.open && format(copyDialog.hours.open, 'h:mm aa')}
          </p>
          <p>
            <strong>Close:</strong>{' '}
            {copyDialog.hours.close && format(copyDialog.hours.close, 'h:mm aa')}
          </p>
        </div>
        <div className="bh-check-list">
          {BUSINESS_DAYS.map((dayKey) =>
            dayKey !== copyDialog.sourceDay ? (
              <label key={dayKey} className="bh-check-row">
                <input
                  type="checkbox"
                  checked={selectedDays[dayKey] || false}
                  onChange={() =>
                    setSelectedDays((prev) => ({
                      ...prev,
                      [dayKey]: !prev[dayKey],
                    }))
                  }
                />
                {BUSINESS_DAY_LABELS[dayKey]}
              </label>
            ) : null
          )}
        </div>
      </BhModal>

      <BhModal
        open={breakCopyDialog.open}
        onClose={() => {
          setBreakCopyDialog((prev) => ({ ...prev, open: false }));
          setSelectedDays({});
        }}
        title="Copy break times"
        titleId={copyBreakTitleId}
        footer={
          <>
            <button
              type="button"
              className="bh-btn bh-btn--ghost"
              onClick={() => {
                setBreakCopyDialog((prev) => ({ ...prev, open: false }));
                setSelectedDays({});
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bh-btn bh-btn--primary"
              onClick={handleApplyBreakTimes}
              disabled={Object.values(selectedDays).filter(Boolean).length === 0}
            >
              Apply to selected
            </button>
          </>
        }
      >
        <p className="bh-modal-intro">Select days to receive this break:</p>
        <div className="bh-modal-summary">
          <p>
            <strong>Start:</strong>{' '}
            {breakCopyDialog.breakTimes.breakStart &&
              format(breakCopyDialog.breakTimes.breakStart, 'h:mm aa')}
          </p>
          <p>
            <strong>End:</strong>{' '}
            {breakCopyDialog.breakTimes.breakEnd &&
              format(breakCopyDialog.breakTimes.breakEnd, 'h:mm aa')}
          </p>
        </div>
        <div className="bh-check-list">
          {BUSINESS_DAYS.map((dayKey) =>
            dayKey !== breakCopyDialog.sourceDay ? (
              <label key={dayKey} className="bh-check-row">
                <input
                  type="checkbox"
                  checked={selectedDays[dayKey] || false}
                  onChange={() =>
                    setSelectedDays((prev) => ({
                      ...prev,
                      [dayKey]: !prev[dayKey],
                    }))
                  }
                />
                {BUSINESS_DAY_LABELS[dayKey]}
              </label>
            ) : null
          )}
        </div>
      </BhModal>
    </div>
  );
};

export default LocationBusinessHoursEditor;
